import React, {useEffect, useRef, useState} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import CommentSection from '../components/CommentSection';
import VideoPlayer from './VideoPlayer';
import AudioPlayer from './AudioPlayer';
import Survey from './Survey';
import api from '../api/axiosInstance';
import { Post } from '../models/types';

const PostContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 95%;
    max-width: 600px;
    margin: 20px auto;
    padding: 20px;
    border: 1px solid #ddd;
    border-radius: 10px;
    background-color: #fff;
    box-shadow: 0 0 10px rgba(245, 102, 7, 0.3);  // Orange shadow
`;

const Heading = styled.h2`
    font-size: 1.5em;
    margin-bottom: 20px;
    text-align: center;
`;

const Description = styled.p`
    font-size: 0.9em;
    margin-bottom: 20px;
    white-space: pre-wrap;
    text-align: left;
    width: 100%;
`;

const Media = styled.div`
    margin-top: 10px;
    width: 100%;
    img, video, audio {
        width: 100%;
        height: auto;
        border-radius: 5px;
    }
`;

const PostDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [post, setPost] = useState<Post | null>(null);
    const navigate = useNavigate();
    const commentsRef = useRef<HTMLDivElement>(null); // Reference to the comments section


    useEffect(() => {
        // Revoked on unmount: an object URL survives the component otherwise, and this page is
        // reached repeatedly from the notification list.
        let objectUrl: string | null = null;

        const fetchPost = async () => {
            try {
                const response = await api.get(`/posts/${id}`);
                const fetchedPost: Post = response.data;

                // `fileType` is the MIME type; `type` is the post CATEGORY (POST / ANKETA /
                // PODCAST / ...). This branched on `type` and so matched nothing, which is why the
                // page rendered "No media available." on every post it has ever shown.
                //
                // Only images are fetched as a blob now. Video and audio are served as HLS by
                // VideoPlayer/AudioPlayer, which stream them by id rather than pulling the whole
                // file into memory first.
                const fileId = fetchedPost.fileResourceIds?.[0];

                if (fetchedPost.fileType?.startsWith('image/') && fileId !== undefined) {
                    // The file id, not the post id: /posts/file/{id} resolves through
                    // fileRepository.findById, so a post id here silently finds the wrong row or
                    // nothing at all.
                    const imageResponse = await api.get(`/posts/file/${fileId}`, { responseType: 'arraybuffer' });
                    const imageBlob = new Blob([imageResponse.data], { type: imageResponse.headers['content-type'] });
                    objectUrl = URL.createObjectURL(imageBlob);
                    setPost({ ...fetchedPost, mediaUrl: objectUrl });
                } else {
                    setPost(fetchedPost);
                }
            } catch (error) {
                console.error('Failed to fetch post details', error);
                navigate('/'); // Redirect to home if the post is not found
            }
        };

        fetchPost();

        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [id, navigate]);

    // Scroll to the comments section when the post is loaded
    useEffect(() => {
        if (post && commentsRef.current) {
            commentsRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [post]);

    if (!post) {
        return <div>Načítavanie...</div>;
    }


    return (
        <PostContainer>
            <Heading>{post.heading}</Heading>
            <Description>{post.description}</Description>
            {/* An ANKETA carries a poll instead of media. Without this branch a poll opened from
                a comment notification showed nothing at all — the same gap the feed already fixed. */}
            {post.type === 'ANKETA' ? (
                <Survey postId={post.id} options={post.surveyOptions} />
            ) : (
                <Media>
                    {post.fileType?.startsWith('video/') && post.fileResourceIds?.length ? (
                        <VideoPlayer videoId={post.fileResourceIds[0]} />
                    ) : post.fileType?.startsWith('audio/') && post.fileResourceIds?.length ? (
                        <AudioPlayer audioId={post.fileResourceIds[0]} />
                    ) : post.fileType?.startsWith('image/') && post.mediaUrl ? (
                        <img src={post.mediaUrl} alt="Post media" />
                    ) : null}
                </Media>
            )}
            <CommentSection autoLoad={true} postId={post.id} commentsCount={post.commentsCount ?? 0} />
        </PostContainer>
    );
};

export default PostDetail;
